import React from 'react';
import {
  Alert,
  Button,
  Chip,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';

import { FDataGrid, FGrid, FGridMoneyCell } from '../grid';
import {
  IDeterminantBudgetManager,
  IDeterminantBudgetManagerRow,
} from './determinantBudget.models';

type Props = {
  manager: IDeterminantBudgetManager;
  loading?: boolean;
  onRefresh: () => void | Promise<void>;
  onOpen: (budget: IDeterminantBudgetManagerRow) => void | Promise<void>;
};

type State = {
  selectedBudgetId: number | null;
}

class DeterminantBudgetManager extends React.Component<Props, State> {
  state: State = {
    selectedBudgetId: null,
  }

  private columns: any[] = [
    {
      field: 'name',
      headerName: 'Determinant Budget',
      flex: 1,
      minWidth: 300,
      renderCell: (p: any) => (
        <Stack spacing={0.2} sx={{ py: 0.5, overflow: 'hidden' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
              {p.row.name}
            </Typography>
            {p.row.iscurrent && (
              <Chip size="small" label="Current" color="info" variant="outlined" />
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary" noWrap>
            {p.row.code}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'statecode',
      headerName: 'State',
      width: 190,
      renderCell: (p: any) => (
        <Chip
          size="small"
          label={p.value || 'DRAFT'}
          color={this.stateColor(p.value)}
        />
      ),
    },
    {
      field: 'scenarioname',
      headerName: 'Source Scenario',
      minWidth: 220,
      flex: 0.7,
    },
    {
      field: 'planningenvelope',
      headerName: 'Planning Envelope',
      width: 190,
      renderCell: FGridMoneyCell,
    },
    {
      field: 'created',
      headerName: 'Created',
      width: 180,
      valueFormatter: (value: any) =>
        value ? new Date(String(value)).toLocaleString() : '-',
    },
    {
      field: 'open',
      headerName: '',
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (p: any) => (
        <Button
          size="small"
          disabled={!p.row.canopen || this.props.loading}
          onClick={() => this.props.onOpen(p.row as IDeterminantBudgetManagerRow)}
        >
          <OpenInNewIcon fontSize="small" />
        </Button>
      ),
    },
  ];
  // [
  //   { field: 'reviewcode', headerName: 'Review', width: 180 },
  //   { field: 'reviewname', headerName: 'Review Name', flex: 1, minWidth: 260 },
  //   { field: 'statecode', headerName: 'State', width: 150 },
  //   { field: 'candidatecount', headerName: 'Candidates', width: 120, type: 'number' },
  //   { field: 'eligiblecount', headerName: 'Eligible', width: 110, type: 'number' },
  //   {
  //     field: 'readinessscore',
  //     headerName: 'Readiness',
  //     width: 120,
  //     valueFormatter: (p: any) => `${Number(p.value || 0)}%`,
  //   },
  //   { field: 'recommendedscenariocode', headerName: 'Recommended', width: 170 },
  //   { field: 'lastactivity', headerName: 'Last Activity', width: 190 },
  // ];

  private stateColor = (statecode?: string): any => {
    switch (String(statecode || '').toUpperCase()) {
      case 'RELEASED_TO_PLANNING':
        return 'success';
      case 'ACCEPTED_FOR_PLANNING':
        return 'info';
      case 'RETURNED':
        return 'warning';
      default:
        return 'default';
    }
  };

  render() {
    const { manager, loading } = this.props;
    const { context, summary, budgets, actions } = manager;

    return (
      <Stack spacing={2}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="caption" color="text.secondary">
                Planning Cycle
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                {context.planningcyclename || context.planningcyclecode}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {context.phasename || context.phasecode}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 1.6 }}>
              <Typography variant="caption" color="text.secondary">Budgets</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>{summary.total || 0}</Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 1.6 }}>
              <Typography variant="caption" color="text.secondary">Draft</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>{summary.draft || 0}</Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 1.6 }}>
              <Typography variant="caption" color="text.secondary">Returned</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>{summary.returned || 0}</Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 1.6 }}>
              <Typography variant="caption" color="text.secondary">Accepted</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>{summary.accepted || 0}</Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 1.6 }}>
              <Typography variant="caption" color="text.secondary">Released</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>{summary.released || 0}</Typography>
            </Grid>
          </Grid>
        </Paper>

        <Alert severity={summary.total > 0 ? 'info' : 'warning'}>
          {summary.total > 0
            ? 'Open a governed Determinant Budget to review its capacity, management decisions, validation and release status.'
            : 'No Determinant Budget has been handed off from Executive Review for this Planning Cycle.'}
        </Alert>

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            disabled={!actions.canrefresh || loading}
            onClick={this.props.onRefresh}
          >
            Refresh
          </Button>
        </Stack>

        {/* <FGrid
          title="Determinant Budgets"
          rows={budgets || []}
          columns={this.columns}
          getRowId={(r: any) => r.rid}
          height={520}
        /> */}
        <FDataGrid
          title="Determinant Budgets"
          subtitle={`${context.planningcyclename || context.planningcyclecode} • ${context.fiscalyear || ''}`}
          rows={budgets || []}
          columns={this.columns}
          loading={loading}
          height={520}
          selectionMode={'single'}
          showSelectionControl
          selectedRowId={this.state.selectedBudgetId}
          onSelectedRowChange={(row) =>
            this.setState({
              selectedBudgetId: row
                ? Number(row.rid)
                : null,
            })
          }
          showQuickFilter
          getRowId={(r: any) => r.rid}
          onRowDoubleClick={(p: any) => p.row.canopen && this.props.onOpen(p.row as IDeterminantBudgetManagerRow)}
          pageSizeOptions={[10, 25, 50]}
        />
      </Stack>
    );
  }
}

export default DeterminantBudgetManager;
